import { useRef } from 'react';
import { Icon } from './Icon';

export interface ChoiceCard<T extends string> {
  value: T;
  title: string;
  hint: string;
  icon: string;
}

// Una scelta fra poche alternative, come schede: un radiogroup vero, con le frecce che spostano
// la scelta e il focus (come i radio nativi).
export function ChoiceCards<T extends string>({
  label,
  items,
  value,
  onChange,
}: {
  label: string;
  items: ChoiceCard<T>[];
  value: T;
  onChange: (v: T) => void;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const move = (from: number, step: number) => {
    const next = (from + step + items.length) % items.length;
    onChange(items[next].value);
    refs.current[next]?.focus();
  };
  return (
    <div role="radiogroup" aria-label={label} style={{ display: 'grid', gap: 8 }}>
      {items.map((it, i) => {
        const on = it.value === value;
        return (
          <button
            key={it.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={on ? 0 : -1}
            onClick={() => onChange(it.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                e.preventDefault();
                move(i, 1);
              } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                e.preventDefault();
                move(i, -1);
              }
            }}
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
              textAlign: 'left',
              padding: on ? 11 : 12,
              border: on ? '2px solid var(--accent)' : '1px solid var(--border)',
              borderRadius: 'var(--r-lg)',
              background: 'var(--panel)',
              color: 'var(--text)',
              cursor: 'pointer',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 16,
                height: 16,
                flex: 'none',
                marginTop: 2,
                borderRadius: '50%',
                border: on ? '5px solid var(--accent)' : '1px solid var(--border-strong)',
              }}
            />
            <span style={{ flex: 1 }}>
              <span
                style={{
                  display: 'flex',
                  gap: 6,
                  alignItems: 'center',
                  font: '600 14px/20px var(--font-ui)',
                }}
              >
                <Icon name={it.icon} size={16} color="var(--accent)" />
                {it.title}
              </span>
              <span className="t-caption dim" style={{ display: 'block', marginTop: 3 }}>
                {it.hint}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
